import type { PocInputValidity } from 'commons/types';
import { debounce } from 'commons/utils/functions.utils';
import { useTranslation } from 'core/i18n/i18n';
import { useEffect, useRef, useState } from 'react';
import { CleaningProperties } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-device-configuration-status-tracking/settings-device-configuration-status-tracking.model';
import { STATUS_TRACKING_CHANGE_DEBOUNCE_MS } from '../../constants';
import {
    CLEANING_THRESHOLDS_ERROR_MESSAGE_KEYS,
    CleaningThresholdsError,
    toCleaningThresholdsValidity,
    validateCleaningThresholds,
} from './cleaning-status-tracking-card.validation';

type EditableCleaningField = Exclude<keyof CleaningProperties, 'supportsOperatorComments'>;

interface UseCleaningStatusTrackingCardProps {
    values: CleaningProperties;
    onValuesChange: (values: CleaningProperties) => void;
    onValidityChange: (isValid: PocInputValidity) => void;
}

export const useCleaningStatusTrackingCard = ({
    values,
    onValuesChange,
    onValidityChange,
}: UseCleaningStatusTrackingCardProps) => {
    const { t } = useTranslation();
    const [cleaningStatus, setCleaningStatus] = useState<CleaningProperties>(values);
    const [validationError, setValidationError] = useState<CleaningThresholdsError>();
    const latestCallbacksRef = useRef({ onValuesChange, onValidityChange });

    const [previousValues, setPreviousValues] = useState(values);
    if (values !== previousValues) {
        setPreviousValues(values);
        setCleaningStatus(values);
    }

    useEffect(() => {
        latestCallbacksRef.current = { onValuesChange, onValidityChange };
    }, [onValuesChange, onValidityChange]);

    const [debouncedCommit] = useState(() =>
        debounce((updatedStatus: CleaningProperties) => {
            const error = validateCleaningThresholds(updatedStatus);
            const { onValuesChange: notifyValuesChange, onValidityChange: notifyValidityChange } =
                latestCallbacksRef.current;

            setValidationError(error);
            notifyValidityChange(toCleaningThresholdsValidity(error));

            if (!error) {
                notifyValuesChange(updatedStatus);
            }
        }, STATUS_TRACKING_CHANGE_DEBOUNCE_MS)
    );

    useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

    const handleChange = <K extends EditableCleaningField>(field: K, value: CleaningProperties[K]) => {
        const updatedStatus = { ...cleaningStatus, [field]: value };

        setCleaningStatus(updatedStatus);
        debouncedCommit(updatedStatus);
    };

    const validity = toCleaningThresholdsValidity(validationError);
    const validationMessage = validationError ? t(CLEANING_THRESHOLDS_ERROR_MESSAGE_KEYS[validationError]) : '';

    return { cleaningStatus, validity, validationMessage, handleChange };
};
